import React, { useState } from 'react';
import PropTypes from 'prop-types';
import CollapsableCard from 'src/common/refactoredComponents/collapsableCard/CollapsableCard';
import {
    AdditionalDetailsRowLabel,
    AdditionalDetailsRowValue,
    AdditionalDetailsCards,
    KeyValueList,
    KeyValueRow,
    TabPanelContent,
} from './BatchPopup.styled';
import { useTranslation } from 'react-i18next';
import { LARGE_TEXT_PX } from 'src/styles/fonts/MCFontConstants';
import { accountNumberZeroTrimmer } from 'src/utils/util';
import { formatPopupDate } from './formatPopupDate';

const formatJoinedValues = (values = [], dateFormat) => {
    const visibleValues = values
        .filter((value) => value !== null && value !== undefined && value !== '')
        .map((value) => formatPopupDate(value, dateFormat));

    return visibleValues.length ? visibleValues.join(' - ') : '';
};

const hasFieldValue = (field) => {
    if (Array.isArray(field.valueLines) && field.valueLines.length) {
        return field.valueLines.some((line) => line.value !== null && line.value !== undefined && line.value !== '');
    }

    return field.value !== null && field.value !== undefined && field.value !== '';
};

const renderFieldValue = (field) => {
    if (Array.isArray(field.valueLines) && field.valueLines.length) {
        return field.valueLines.map((line, index) => (
            <React.Fragment key={`${field.label}-${index}`}>
                {line.value}
                {index < field.valueLines.length - 1 ? <br /> : null}
            </React.Fragment>
        ));
    }

    return field.value;
};

const renderFields = (fields) => (
    <KeyValueList $columns={3}>
        {fields.filter(hasFieldValue).map((field) => (
            <KeyValueRow key={field.label} $columns={3}>
                <AdditionalDetailsRowLabel>{field.label}</AdditionalDetailsRowLabel>
                <AdditionalDetailsRowValue>{renderFieldValue(field)}</AdditionalDetailsRowValue>
            </KeyValueRow>
        ))}
    </KeyValueList>
);

const AdditionalDetailsTab = ({ content }) => {
    const { t } = useTranslation();
    const dateFormat = t('dateFormat');
    const additionalDetails = content && typeof content === 'object' && !Array.isArray(content) ? content : {};

    const sectionContent = [
        {
            title: t('additionalDetailsTabs.details'),
            fields: [
                {
                    label: t('additionalDetailsTabs.account'),
                    valueLines: [
                        { value: additionalDetails.homeLocation?.homeAccName },
                        { value: accountNumberZeroTrimmer(additionalDetails.homeLocation?.homeAccNum) },
                    ],
                },
                { label: t('additionalDetailsTabs.lastUse'), value: formatPopupDate(additionalDetails.lastUse?.previousSurgeryDate, dateFormat) },
                { label: t('additionalDetailsTabs.daysOut'), value: additionalDetails.daysOut },
                { label: t('additionalDetailsTabs.dailyRate'), value: additionalDetails.dailyRate },
            ],
        },
        {
            title: t('additionalDetailsTabs.qualityDetails'),
            fields: [
                {
                    label: t('additionalDetailsTabs.lastFieldQualityInspection'),
                    value: formatPopupDate(additionalDetails.lastFieldQuallityInspectionDate, dateFormat),
                },
                {
                    label: t('additionalDetailsTabs.lastDcQualityInspection'),
                    value: formatPopupDate(additionalDetails.lastDCQuallityInspectionDate, dateFormat),
                },
                {
                    label: t('additionalDetailsTabs.lastFieldQualityUser'),
                    value: formatJoinedValues(Object.values(additionalDetails.lastFieldQualityUser || {}), dateFormat),
                },
            ],
        },
        {
            title: t('additionalDetailsTabs.recentData'),
            fields: [
                {
                    label: t('additionalDetailsTabs.mostRecentMycaseReference'),
                    value: additionalDetails.lastUse?.previousSurgerysstRefNo,
                },
                {
                    label: t('additionalDetailsTabs.mostRecentSapOrder'),
                    value: additionalDetails?.previousSurgerySapOrderNo,
                },
                {
                    label: t('additionalDetailsTabs.mostRecentAccount'),
                    valueLines: [
                        { value: additionalDetails.mostRecentMyCaseReference?.previousSurgeryAccountName },
                        { value: accountNumberZeroTrimmer(additionalDetails.mostRecentMyCaseReference?.previousSurgeryAccountNumber) },
                    ],
                },
                {
                    label: t('additionalDetailsTabs.mostRecentSapDelivery'),
                    value: additionalDetails.previousSurgeryDeliveryId,
                },
                {
                    label: t('additionalDetailsTabs.mostRecentCarrierTracking'),
                    value: additionalDetails.previousSurgeryTrackingNumSet,
                }       
             ],
        },
    ];

    const visibleSections = sectionContent.filter((section) => section.fields.some(hasFieldValue));

    const [expandedSections, setExpandedSections] = useState([true, true, true]);

    const toggleSection = (index) => {
        setExpandedSections((currentSections) => currentSections.map((isExpanded, currentIndex) => (
            currentIndex === index ? !isExpanded : isExpanded
        )));
    };

    return (
        <TabPanelContent>
            <AdditionalDetailsCards>
                {visibleSections.map((section, index) => (
                    <CollapsableCard
                        key={`${section.title}-${index}`}
                        title={section.title}
                        isExpanded={expandedSections[index]}
                        onClick={() => toggleSection(index)}
                        fontSize={LARGE_TEXT_PX}
                        fontSizeMobile={LARGE_TEXT_PX}
                        borderRadius="6px"
                        boxShadow="0px 0px 6px #00000029"
                        margin="0"
                        marginBottom="12px"
                        mbMarginBottom="12px"
                        mobileMargin="5px 0 8px 0"
                        padding="16px 18px"
                        editButton={false}
                    >
                        {renderFields(section.fields)}
                    </CollapsableCard>
                ))}
            </AdditionalDetailsCards>
        </TabPanelContent>
    );
};

AdditionalDetailsTab.propTypes = {
    content: PropTypes.object,
};

AdditionalDetailsTab.defaultProps = {
    content: null,
};

export default AdditionalDetailsTab;